import React, { useState } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';

// Define styled components
const AppCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 16px rgba(55, 91, 210, 0.2)',
  },
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'hidden',
}));

const AppIcon = styled(Box)(({ theme }) => ({
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  transition: 'transform 0.3s ease, color 0.3s ease',
  '.app-card:hover &': {
    transform: 'scale(1.2)',
    color: theme.palette.primary.dark,
  },
}));

const CategoryHeader = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  paddingBottom: theme.spacing(1),
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: '2px',
  }
}));

// Simple icons using HTML entities or emoji
const getIconSymbol = (iconName) => {
  const iconMap = {
    "faMapMarkerAlt": "📍", 
    "faBuildingColumns": "🏛️", 
    "faGlobe": "🌐", 
    "faIdCard": "🪪", 
    "faUpload": "⬆️", 
    "faClock": "⏱️",
    "faGavel": "⚖️",
    "faLock": "🔒",
    "faScaleBalanced": "⚖️",
    "faBriefcase": "💼",
    "faBookOpen": "📖",
    "faFileContract": "📝",
    "faReceipt": "🧾",
    "faFolderOpen": "📂",
    "faHandshake": "🤝",
    "faPaperclip": "📎",
    "faShieldHalved": "🛡️",
    "faLayerGroup": "📚",
    "faCertificate": "🏅",
    "faSignature": "✍️",
    "faMagnifyingGlass": "🔍",
    "faFingerprint": "👆",
    "faDatabase": "💾",
    "faHandshakeAngle": "🤲",
    "faFileInvoice": "📄"
  };
  
  return iconMap[iconName] || "📎"; // Default to paperclip if not found
};

const AppLinks = () => {
  const theme = useTheme();
  const [hoveredId, setHoveredId] = useState(null);
  
  // Group the apps by category
  const appCategories = [
    {
      name: "Navigation & Entry Points",
      apps: [
        {
          id: 1,
          href: "https://nav.freeweb3.com",
          title: "Nav.FreeWeb3",
          description: "Central navigation hub for all FreeWeb3 applications",
          icon: "faMapMarkerAlt",
          category: "core",
        },
        {
          id: 2,
          href: "https://app.freeweb3.com/",
          title: "FreeWeb3 Hub",
          description: "Main dashboard for accessing all FreeWeb3 tools",
          icon: "faBuildingColumns",
          category: "core",
        },
        {
          id: 3,
          href: "https://freeweb3.com",
          title: "FreeWeb3",
          description: "FreeWeb3 main portal and blockchain entry point",
          icon: "faGlobe",
          category: "core",
        },
      ]
    },
    {
      name: "User Identity & Storage",
      apps: [
        {
          id: 4,
          href: "https://keypairs.freeweb3.com",
          title: "Web3 ID",
          description: "Create and manage your decentralized identity",
          icon: "faIdCard",
          category: "identity",
        },
        {
          id: 5,
          href: "https://upload.freeweb3.com/",
          title: "Public Document Upload",
          description: "Upload public documents to IPFS for permanent storage",
          icon: "faUpload",
          category: "storage",
        },
        {
          id: 8,
          href: "https://upload-encrypted.freeweb3.com/",
          title: "Encrypted File Storage",
          description: "Encrypt and store sensitive files on IPFS",
          icon: "faLock",
          category: "storage",
        },
        {
          id: 18,
          href: "https://upload-encrypted.freeweb3.com/",
          title: "Image Encrypt & Upload",
          description: "Specialized tool for encrypting and uploading images",
          icon: "faShieldHalved",
          category: "storage",
        },
      ]
    },
    {
      name: "Evidencer Tools",
      apps: [
        {
          id: 10,
          href: "https://hero.evidencer.io/",
          title: "Evidencer Hub",
          description: "Central dashboard for Evidencer applications",
          icon: "faBriefcase",
          category: "evidencer",
        },
        {
          id: 11,
          href: "https://upload-encrypted.freeweb3.com/",
          title: "Evidencer Forms Instructions",
          description: "Instructions for using Evidencer forms",
          icon: "faBookOpen",
          category: "evidencer",
        },
        {
          id: 12,
          href: "https://nav.evidencer.io/",
          title: "Evidencer Forms",
          description: "Access all Evidencer legal forms",
          icon: "faFileContract",
          category: "evidencer",
        },
        {
          id: 13,
          href: "https://esi.evidencer.freeweb3.com/",
          title: "ESI Manager",
          description: "Electronically Stored Information manager",
          icon: "faReceipt",
          category: "evidencer",
        },
        {
          id: 14,
          href: "https://claims.evidencer.freeweb3.com/",
          title: "Claim Manager",
          description: "Create and manage legal claims",
          icon: "faFolderOpen",
          category: "evidencer",
        },
        {
          id: 15,
          href: "https://nav.freeweb3.com",
          title: "Claim Resolution",
          description: "Dispute resolution platform for claims",
          icon: "faHandshake",
          category: "evidencer",
        },
        {
          id: 26,
          href: "https://adr.freeweb3.com/",
          title: "Alternative Dispute Resolution",
          description: "ADR platform for resolving legal disputes",
          icon: "faHandshakeAngle",
          category: "evidencer",
        },
      ]
    },
    {
      name: "Blockchain Integration",
      apps: [
        {
          id: 6,
          href: "https://cidtochain.evidencer.io/",
          title: "Time-Stamp Files",
          description: "Time-stamp files on the blockchain",
          icon: "faClock",
          category: "blockchain",
        },
        {
          id: 19,
          href: "https://cidtochain.evidencer.io/",
          title: "Evidence to Blockchain I",
          description: "Register evidence on the blockchain",
          icon: "faLayerGroup",
          category: "blockchain",
        },
        {
          id: 20,
          href: "https://free-cid.evidencer.io/",
          title: "Evidence to Blockchain II",
          description: "Free tier for blockchain evidence registration",
          icon: "faLayerGroup",
          category: "blockchain",
        },
        {
          id: 21,
          href: "https://artifacts.freeweb3.com",
          title: "Mint Digital Artifacts",
          description: "Create NFT artifacts for legal evidence",
          icon: "faCertificate",
          category: "blockchain",
        },
        {
          id: 22,
          href: "https://artifacts.evidencer.io",
          title: "Register a Claim",
          description: "Register legal claims as NFTs",
          icon: "faSignature",
          category: "blockchain",
        },
      ]
    },
    {
      name: "Evidence & Query Tools",
      apps: [
        {
          id: 7,
          href: "https://freeweb3.infura-ipfs.io/ipfs/QmeuA7NVm4LKPF9eyyXfWhQiHNhyX4GZHXJXNzUfmnsvp9",
          title: "Restraining Order Video",
          description: "Video guide for restraining orders",
          icon: "faGavel",
          category: "resources",
        },
        {
          id: 9,
          href: "https://freeweb3.infura-ipfs.io/ipfs/QmS6oCuxnYYzjo8SyXBeN3XWwbGCqYASubaufvcGaUrwUD",
          title: "Civil Rights Video",
          description: "Video guide on civil rights claims",
          icon: "faScaleBalanced",
          category: "resources",
        },
        {
          id: 23,
          href: "https://query.evidencer.io",
          title: "Query Evidencer",
          description: "Search platform for Evidencer data",
          icon: "faMagnifyingGlass",
          category: "query",
        },
        {
          id: 24,
          href: "https://dbsa.freeweb3.com",
          title: "Query Evidencer DBSA",
          description: "Database-specific application for queries",
          icon: "faFingerprint",
          category: "query",
        },
        {
          id: 25,
          href: "https://encrypted-query.evidencer.io/",
          title: "Artifact Dashboard",
          description: "Dashboard for managing digital artifacts",
          icon: "faDatabase",
          category: "query",
        },
      ]
    },
    {
      name: "External Resources",
      apps: [
        {
          id: 16,
          href: "https://evidencer.io",
          title: "Evidencer.io",
          description: "Main Evidencer external site",
          icon: "faPaperclip",
          category: "external",
        },
        {
          id: 17,
          href: "https://hero.evidencer.io/",
          title: "Hub.Evidencer.io",
          description: "External hub for Evidencer applications",
          icon: "faGlobe",
          category: "external",
        },
        {
          id: 27,
          href: "https://nav.evidencer.io/",
          title: "Evidencer Forms & Instructions",
          description: "Comprehensive guide to all Evidencer forms",
          icon: "faFileInvoice",
          category: "external",
        },
      ]
    }
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h2" 
        align="center" 
        sx={{ 
          fontWeight: 700,
          mb: 4,
          color: theme.palette.secondary.main,
          position: 'relative',
          display: 'inline-block',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px', 
            height: '4px',
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            borderRadius: '2px',
          }
        }}
      >
        Explore Our Applications
      </Typography>
      
      <Box sx={{ mt: 6 }}>
        {appCategories.map((category, idx) => (
          <Box key={idx} sx={{ mb: 5 }}>
            <CategoryHeader variant="h5" component="h3">
              {category.name}
            </CategoryHeader>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {category.apps.map((app) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={app.id}>
                  <AppCard
                    className="app-card"
                    elevation={2}
                    onMouseEnter={() => setHoveredId(app.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <CardActionArea 
                      component="a"
                      href={app.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 2
                      }}
                    >
                      <AppIcon sx={{ 
                        transform: hoveredId === app.id ? 'scale(1.2)' : 'scale(1)',
                        color: hoveredId === app.id ? theme.palette.primary.dark : theme.palette.primary.main,
                        fontSize: '2rem'
                      }}>
                        {getIconSymbol(app.icon)}
                      </AppIcon>
                      
                      <CardContent sx={{ flexGrow: 1, width: '100%', textAlign: 'center' }}>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {app.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                          {app.description}
                        </Typography>
                      </CardContent>
                      
                      <Chip 
                        label={app.category} 
                        size="small"
                        color={hoveredId === app.id ? "primary" : "default"}
                        sx={{ mb: 1 }}
                      />
                    </CardActionArea>
                  </AppCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AppLinks;